import { useEffect, useMemo, useState } from "react";
import { format, addDays, differenceInCalendarDays } from "date-fns";
import {
  X,
  Loader2,
  AlertCircle,
  ArrowRight,
  Minus,
  Plus,
  Trash2,
} from "lucide-react";

import { getAllCourses } from "@/services/courseService";
import { getAllFerries } from "@/services/ferryService";
import { getAllTransport } from "@/services/transportService";
import { getAllAccommodation } from "@/services/accommodationService";
import { updateBooking } from "@/services/bookingService";
import {
  calculateCourseRoundPrice,
  calculateAccommodationTotal,
  calculateFerryTotal,
  calculateTransportTotal,
  deriveTeePeriod,
  SHARING_TYPES,
} from "@/utils/pricingEngine";

/* ---------- small helpers ---------- */

const toDate = (v) => {
  if (!v) return undefined;
  const d = v?.toDate ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d;
};
const toInputDate = (v) => {
  const d = toDate(v);
  return d ? format(d, "yyyy-MM-dd") : "";
};
const fromInputDate = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n) =>
  `SGD ${r2(n).toLocaleString("en-SG", { maximumFractionDigits: 2 })}`;
const signed = (n) =>
  n === 0 ? money(0) : `${n > 0 ? "+" : "−"}${money(Math.abs(n))}`;

// Unchanged item -> keep the snapshot saved on the booking (price at booking
// time). Changed item -> use the live catalog entry.
const pick = (snapshot, id, list) =>
  snapshot && snapshot.id === id
    ? snapshot
    : list.find((x) => x.id === id) || snapshot || null;

const withCurrent = (list, snapshot) =>
  snapshot && !list.some((x) => x.id === snapshot.id)
    ? [snapshot, ...list]
    : list;

/* ---------- form <-> booking ---------- */

function initialForm(booking) {
  const checkIn = toDate(booking.checkInDate);
  const checkOut = toDate(booking.checkOutDate);
  const nights =
    checkIn && checkOut
      ? Math.max(0, differenceInCalendarDays(checkOut, checkIn))
      : booking.selectedAccommodation
        ? booking.selectedPackage?.nights || 1
        : 0;

  return {
    numPlayers: booking.numPlayers || 1,
    rounds: (booking.selectedCourses || []).map((c, i) => ({
      courseId: c.id,
      date: toInputDate(booking.roundDates?.[i]),
      time: booking.roundTimes?.[i] || "",
    })),
    accommodationId: booking.selectedAccommodation?.id || "",
    sharingType: booking.sharingType || SHARING_TYPES.SINGLE,
    // Bookings from before the room selector were charged one room per player.
    numRooms: booking.numRooms || booking.numPlayers || 1,
    nights,
    checkIn: toInputDate(booking.checkInDate || booking.roundDates?.[0]),
    ferryId: booking.selectedFerry?.id || "",
    ferryTime: booking.selectedFerryTime || "",
    outboundFerryTime: booking.selectedOutboundFerryTime || "",
    transportId: booking.selectedTransport?.id || "",
    transportTime: booking.selectedTransportTime || "",
    outboundTransportTime: booking.selectedOutboundTransportTime || "",
  };
}

function resolve(form, booking, cat) {
  const rounds = form.rounds.map((r, i) => ({
    course: pick(booking.selectedCourses?.[i], r.courseId, cat.courses),
    date: fromInputDate(r.date),
    time: r.time,
  }));
  const start = fromInputDate(form.checkIn || form.rounds[0]?.date);
  const nightDates =
    start && form.nights > 0
      ? Array.from({ length: form.nights }, (_, i) => addDays(start, i))
      : [];
  return {
    players: form.numPlayers,
    rounds,
    nightDates,
    accommodation:
      form.nights > 0 && form.accommodationId
        ? pick(
            booking.selectedAccommodation,
            form.accommodationId,
            cat.accommodations,
          )
        : null,
    sharingType: form.sharingType,
    numRooms: form.numRooms,
    ferry: form.ferryId
      ? pick(booking.selectedFerry, form.ferryId, cat.ferries)
      : null,
    transport: form.transportId
      ? pick(booking.selectedTransport, form.transportId, cat.transports)
      : null,
  };
}

/* ---------- price breakdown (one line per priced item) ---------- */

function buildBreakdown(s) {
  const lines = [];
  const numRounds = s.rounds.length;

  s.rounds.forEach((r, i) => {
    if (!r.course) return;
    const perPlayer = calculateCourseRoundPrice({
      course: r.course,
      date: r.date,
      period: deriveTeePeriod(r.time),
      totalPlayers: s.players,
      totalRounds: numRounds,
    });
    lines.push({
      key: `round-${i}`,
      label: `Round ${i + 1}`,
      detail: `${r.course.name} · ${r.date ? format(r.date, "d MMM") : "no date"}${r.time ? ` ${r.time}` : ""} · ${money(perPlayer)} per player × ${s.players} player${s.players > 1 ? "s" : ""}`,
      amount: r2(perPlayer * s.players),
    });
  });

  if (s.accommodation && s.nightDates.length) {
    const { total } = calculateAccommodationTotal({
      accommodation: s.accommodation,
      nightDates: s.nightDates,
      sharingType: s.sharingType,
      numRooms: s.numRooms,
    });
    lines.push({
      key: "stay",
      label: "Accommodation",
      detail: `${s.accommodation.title} · ${s.nightDates.length} night${s.nightDates.length > 1 ? "s" : ""} · ${s.numRooms} room${s.numRooms > 1 ? "s" : ""}`,
      amount: r2(total),
    });
  }

  if (s.ferry) {
    lines.push({
      key: "ferry",
      label: "Ferry",
      detail: `${s.ferry.title} · inbound + outbound × ${s.players} guest${s.players > 1 ? "s" : ""}`,
      amount: r2(
        calculateFerryTotal({ ferry: s.ferry, totalGuests: s.players }),
      ),
    });
  }

  if (s.transport) {
    const first = s.rounds[0];
    const { total } = calculateTransportTotal({
      course: first?.course,
      vehicle: s.transport,
      totalGuests: s.players,
      numRounds,
      date: first?.date,
      period: deriveTeePeriod(first?.time),
    });
    lines.push({
      key: "transport",
      label: "Land transport",
      detail: s.transport.type
        ? `${s.transport.title} · ${s.transport.type}`
        : s.transport.title,
      amount: r2(total),
    });
  }
  return lines;
}

function diffLines(oldLines, newLines) {
  const keys = [];
  [...oldLines, ...newLines].forEach((l) => {
    if (!keys.includes(l.key)) keys.push(l.key);
  });
  return keys.map((key) => {
    const o = oldLines.find((l) => l.key === key);
    const n = newLines.find((l) => l.key === key);
    const oldAmt = o?.amount ?? 0;
    const newAmt = n?.amount ?? 0;
    return {
      key,
      label: (n || o).label,
      oldDetail: o?.detail || "Not included",
      newDetail: n?.detail || "Removed",
      oldAmt,
      newAmt,
      delta: r2(newAmt - oldAmt),
      changed: oldAmt !== newAmt || (o?.detail || "") !== (n?.detail || ""),
    };
  });
}

/* ---------- UI bits ---------- */

const inputCls =
  "h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase text-gray-400 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-100 p-4 space-y-3">
      <p className="text-xs font-bold uppercase tracking-wide text-[#0B2421]">
        {title}
      </p>
      {children}
    </div>
  );
}

function Stepper({ value, onChange, min = 0, max = 99 }) {
  return (
    <div className="flex items-center gap-2 h-10">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30"
      >
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="w-6 text-center font-bold text-[#0B2421]">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center disabled:opacity-30"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ---------- modal ---------- */

export default function EditBookingModal({
  booking,
  onClose,
  onSaved,
  onDelete,
}) {
  const [cat, setCat] = useState({
    courses: [],
    ferries: [],
    transports: [],
    accommodations: [],
  });
  const [loadingCat, setLoadingCat] = useState(true);
  const [form, setForm] = useState(() => initialForm(booking));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const original = useMemo(() => initialForm(booking), [booking]);

  useEffect(() => {
    (async () => {
      try {
        const [courses, ferries, transports, accommodations] =
          await Promise.all([
            getAllCourses(),
            getAllFerries(),
            getAllTransport(),
            getAllAccommodation(),
          ]);
        setCat({
          courses: courses || [],
          ferries: ferries || [],
          transports: transports || [],
          accommodations: accommodations || [],
        });
      } catch (err) {
        console.error("Failed to load catalog:", err);
        setError("Could not load options. Changing items may not work.");
      } finally {
        setLoadingCat(false);
      }
    })();
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setRound = (i, patch) =>
    setForm((f) => ({
      ...f,
      rounds: f.rounds.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    }));

  // Before = booking as saved. After = booking with the edits applied.
  const oldState = useMemo(
    () => resolve(original, booking, cat),
    [original, booking, cat],
  );
  const newState = useMemo(
    () => resolve(form, booking, cat),
    [form, booking, cat],
  );
  const rows = useMemo(
    () => diffLines(buildBreakdown(oldState), buildBreakdown(newState)),
    [oldState, newState],
  );

  const oldComputed = r2(rows.reduce((s, r) => s + r.oldAmt, 0));
  const newComputed = r2(rows.reduce((s, r) => s + r.newAmt, 0));
  const delta = r2(newComputed - oldComputed);
  const storedTotal = Number(booking.grandTotal) || 0;
  // Anything on the original total that isn't a priced line (fees, discounts)
  const otherCharges = r2(storedTotal - oldComputed);
  const newGrandTotal = r2(storedTotal + delta);

  const timeFields = [
    ["ferryTime", "Inbound ferry time"],
    ["outboundFerryTime", "Outbound ferry time"],
    ["transportTime", "Inbound pickup time"],
    ["outboundTransportTime", "Outbound pickup time"],
  ];
  const timeChanges = timeFields
    .filter(([k]) => form[k] !== original[k])
    .map(([k, label]) => ({
      label,
      from: original[k] || "-",
      to: form[k] || "-",
    }));

  const changedRows = rows.filter((r) => r.changed);
  const hasChanges =
    changedRows.length > 0 ||
    timeChanges.length > 0 ||
    JSON.stringify(form) !== JSON.stringify(original);
  const missingDate = form.rounds.some((r) => !r.date);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const s = newState;
      const first = s.rounds[0];
      const last = s.rounds[s.rounds.length - 1];
      const hasStay = !!s.accommodation && s.nightDates.length > 0;

      const arrival = first?.date ?? null;

      const patch = {
        numPlayers: form.numPlayers,
        selectedCourses: s.rounds.map((r) => r.course),
        roundDates: s.rounds.map((r) => r.date ?? null),
        roundTimes: s.rounds.map((r) => r.time || null),
        startDate: first?.date ?? null,
        endDate: last?.date ?? null,
        selectedAccommodation: s.accommodation ?? null,
        sharingType: hasStay ? form.sharingType : null,
        numRooms: hasStay ? form.numRooms : null,
        checkInDate: hasStay ? s.nightDates[0] : arrival,
        checkOutDate: hasStay ? addDays(s.nightDates[0], form.nights) : null,
        checkInTime: hasStay ? booking.checkInTime || "14:00" : null,
        selectedFerry: s.ferry ?? null,
        selectedFerryTime: form.ferryTime || null,
        selectedOutboundFerryTime: form.outboundFerryTime || null,
        selectedTransport: s.transport ?? null,
        selectedTransportTime: form.transportTime || null,
        selectedOutboundTransportTime: form.outboundTransportTime || null,
        selectedPackage: {
          ...(booking.selectedPackage || {}),
          nights: form.nights,
        },
        grandTotal: newGrandTotal,
        // Keep the itemized subtotals in step with the new grand total so the
        // customer's booking details don't show stale amounts.
        priceBreakdown: (() => {
          const sum = (pred) =>
            r2(
              buildBreakdown(s)
                .filter((l) => pred(l.key))
                .reduce((t, l) => t + l.amount, 0),
            );
          return {
            golfTotal: sum((k) => k.startsWith("round-")),
            accommodationTotal: sum((k) => k === "stay"),
            ferryTotal: sum((k) => k === "ferry"),
            transportTotal: sum((k) => k === "transport"),
          };
        })(),
      };
      if (!s.transport) patch.selectedOutboundTransport = null;

      const historyEntry = {
        previousTotal: storedTotal,
        newTotal: newGrandTotal,
        difference: delta,
        reason: reason.trim() || null,
        priceChanges: changedRows.map((r) => ({
          item: r.label,
          from: r.oldDetail,
          to: r.newDetail,
          oldAmount: r.oldAmt,
          newAmount: r.newAmt,
          difference: r.delta,
        })),
        timeChanges,
      };

      await updateBooking(booking.id, patch, historyEntry);
      onSaved(booking.id, patch);
    } catch (err) {
      console.error("Failed to update booking:", err);
      setError(err?.message || "Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const courseOptions = (i) =>
    withCurrent(cat.courses, booking.selectedCourses?.[i]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#0B2421]">Edit Booking</h3>
            <p className="text-xs text-gray-400">
              {booking.customerName || "Guest"} ·{" "}
              {booking.reservationNumber || booking.id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loadingCat ? (
            <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading options...
            </div>
          ) : (
            <>
              {/* <Section title="Players">
                <Stepper
                  value={form.numPlayers}
                  onChange={(v) => set({ numPlayers: v })}
                  min={1}
                  max={8}
                />
                <p className="text-[11px] text-gray-400">
                  Golfer names/passports aren't edited here.
                </p>
              </Section> */}

              <Section title="Rounds">
                {form.rounds.map((r, i) => {
                  const course = newState.rounds[i]?.course;
                  const slots = course?.tee_slots || [];
                  const times = slots.map((s) => s.time);
                  if (r.time && !times.includes(r.time)) times.unshift(r.time);
                  return (
                    <div
                      key={i}
                      className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr] gap-2"
                    >
                      <Field label={`Round ${i + 1} course`}>
                        <select
                          className={inputCls}
                          value={r.courseId || ""}
                          onChange={(e) =>
                            setRound(i, { courseId: e.target.value })
                          }
                        >
                          {courseOptions(i).map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Date">
                        <input
                          type="date"
                          className={inputCls}
                          value={r.date}
                          onChange={(e) =>
                            setRound(i, { date: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="Tee time (ID)">
                        {times.length ? (
                          <select
                            className={inputCls}
                            value={r.time}
                            onChange={(e) =>
                              setRound(i, { time: e.target.value })
                            }
                          >
                            <option value="">Select</option>
                            {times.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="time"
                            className={inputCls}
                            value={r.time}
                            onChange={(e) =>
                              setRound(i, { time: e.target.value })
                            }
                          />
                        )}
                      </Field>
                    </div>
                  );
                })}
              </Section>

              <Section title="Accommodation">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Field label="Hotel">
                    <select
                      className={inputCls}
                      value={form.accommodationId}
                      onChange={(e) => set({ accommodationId: e.target.value })}
                    >
                      <option value="">None</option>
                      {withCurrent(
                        cat.accommodations,
                        booking.selectedAccommodation,
                      ).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.title}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="No. of rooms">
                    <Stepper
                      value={form.numRooms}
                      onChange={(v) => set({ numRooms: v })}
                      min={1}
                      max={Math.max(form.numPlayers, form.numRooms)}
                    />
                  </Field>
                  <Field label="Check-in date">
                    <input
                      type="date"
                      className={inputCls}
                      value={form.checkIn}
                      onChange={(e) => set({ checkIn: e.target.value })}
                    />
                  </Field>
                  <Field label="Nights">
                    <Stepper
                      value={form.nights}
                      onChange={(v) => set({ nights: v })}
                      min={0}
                      max={14}
                    />
                  </Field>
                </div>
              </Section>

              <Section title="Ferry">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Field label="Route">
                    <select
                      className={inputCls}
                      value={form.ferryId}
                      onChange={(e) => set({ ferryId: e.target.value })}
                    >
                      <option value="">None</option>
                      {withCurrent(cat.ferries, booking.selectedFerry).map(
                        (f) => (
                          <option key={f.id} value={f.id}>
                            {f.title}
                          </option>
                        ),
                      )}
                    </select>
                  </Field>
                  <Field label="Inbound departs (SG)">
                    <input
                      type="time"
                      className={inputCls}
                      value={form.ferryTime}
                      onChange={(e) => set({ ferryTime: e.target.value })}
                    />
                  </Field>
                  <Field label="Outbound departs (ID)">
                    <input
                      type="time"
                      className={inputCls}
                      value={form.outboundFerryTime}
                      onChange={(e) =>
                        set({ outboundFerryTime: e.target.value })
                      }
                    />
                  </Field>
                </div>
              </Section>

              <Section title="Land transport">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Field label="Vehicle">
                    <select
                      className={inputCls}
                      value={form.transportId}
                      onChange={(e) => set({ transportId: e.target.value })}
                    >
                      <option value="">None / included</option>
                      {withCurrent(
                        cat.transports,
                        booking.selectedTransport,
                      ).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.type ? `${t.title} · ${t.type}` : t.title}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Inbound pickup (ID)">
                    <input
                      type="time"
                      className={inputCls}
                      value={form.transportTime}
                      onChange={(e) => set({ transportTime: e.target.value })}
                    />
                  </Field>
                  <Field label="Return pickup (ID)">
                    <input
                      type="time"
                      className={inputCls}
                      value={form.outboundTransportTime}
                      onChange={(e) =>
                        set({ outboundTransportTime: e.target.value })
                      }
                    />
                  </Field>
                </div>
              </Section>

              {/* ---------- price calculation ---------- */}
              <div className="rounded-xl border border-[#0B2421]/15 overflow-hidden">
                <div className="bg-[#0B2421] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide">
                  Price calculation
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase text-gray-400 border-b border-gray-100">
                        <th className="px-4 py-2">Item</th>
                        <th className="px-4 py-2 text-right">Before</th>
                        <th className="px-4 py-2 text-right">After</th>
                        <th className="px-4 py-2 text-right">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.key}
                          className={`border-b border-gray-50 align-top ${r.changed ? "bg-amber-50" : ""}`}
                        >
                          <td className="px-4 py-3">
                            <p className="font-semibold text-[#0B2421]">
                              {r.label}
                            </p>
                            {r.changed ? (
                              <div className="mt-1 text-xs space-y-0.5">
                                <p className="text-gray-400 line-through">
                                  {r.oldDetail}
                                </p>
                                <p className="text-amber-800 flex items-start gap-1">
                                  <ArrowRight className="h-3 w-3 mt-0.5 shrink-0" />
                                  {r.newDetail}
                                </p>
                              </div>
                            ) : (
                              <p className="mt-1 text-xs text-gray-400">
                                {r.newDetail}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500 whitespace-nowrap">
                            {money(r.oldAmt)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-gray-700 whitespace-nowrap">
                            {money(r.newAmt)}
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-bold whitespace-nowrap ${
                              r.delta > 0
                                ? "text-red-600"
                                : r.delta < 0
                                  ? "text-emerald-700"
                                  : "text-gray-300"
                            }`}
                          >
                            {r.delta === 0 ? "—" : signed(r.delta)}
                          </td>
                        </tr>
                      ))}
                      {otherCharges !== 0 && (
                        <tr className="border-b border-gray-50">
                          <td className="px-4 py-3 text-xs text-gray-400">
                            Other charges / discounts on original booking
                            (unchanged)
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500">
                            {money(otherCharges)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">
                            {money(otherCharges)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-300">
                            —
                          </td>
                        </tr>
                      )}
                      <tr className="bg-gray-50 font-bold text-[#0B2421]">
                        <td className="px-4 py-3">Total</td>
                        <td className="px-4 py-3 text-right">
                          {money(storedTotal)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {money(newGrandTotal)}
                        </td>
                        <td
                          className={`px-4 py-3 text-right ${delta > 0 ? "text-red-600" : delta < 0 ? "text-emerald-700" : "text-gray-400"}`}
                        >
                          {delta === 0 ? "—" : signed(delta)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {timeChanges.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-500 space-y-0.5">
                    <p className="font-semibold text-gray-600">
                      Time changes (no price impact)
                    </p>
                    {timeChanges.map((t) => (
                      <p key={t.label}>
                        {t.label}: {t.from} → {t.to}
                      </p>
                    ))}
                  </div>
                )}

                {delta !== 0 && (
                  <div
                    className={`px-4 py-3 text-sm font-semibold border-t ${
                      delta > 0
                        ? "bg-red-50 border-red-100 text-red-700"
                        : "bg-emerald-50 border-emerald-100 text-emerald-800"
                    }`}
                  >
                    {delta > 0
                      ? `Customer owes an extra ${money(delta)}`
                      : `${money(Math.abs(delta))} to be refunded to the customer`}
                    <span className="block text-xs font-normal opacity-80">
                      Settled outside the system — this only updates the booking
                      total.
                    </span>
                  </div>
                )}
              </div>

              <Field label="Reason for edit (optional, saved in edit history)">
                <input
                  className={inputCls}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Customer asked to move Round 2 to Sunday"
                />
              </Field>
            </>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <div>
            <p className="text-[11px] uppercase text-gray-400">New total</p>
            <p className="text-lg font-bold text-[#0B2421]">
              {money(newGrandTotal)}
              {delta !== 0 && (
                <span
                  className={`ml-2 text-sm ${delta > 0 ? "text-red-600" : "text-emerald-700"}`}
                >
                  ({signed(delta)})
                </span>
              )}
            </p>
          </div>

          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              className="h-11 px-4 rounded-xl border border-red-200 text-red-600 font-semibold hover:bg-red-50 disabled:opacity-40 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">Delete</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-11 px-5 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-100 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loadingCat || !hasChanges || missingDate}
              className="h-11 px-6 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
